import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Schedule as ScheduleIcon } from '@mui/icons-material';
import { scheduleAPI, studentAPI, classAPI, memoAPI } from '../services/api';

const ScheduleManagement = () => {
  const [schedules, setSchedules] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [memo, setMemo] = useState('');
  const [memoLastUpdated, setMemoLastUpdated] = useState(null);
  
  const [formData, setFormData] = useState({
    student_id: '',
    class_id: '',
    start_time: '14:00',
  });

  const days = ['화', '수', '목', '금', '토']; // 일요일, 월요일 제외
  const timeSlots = [];
  
  // 30분 단위로 시간 슬롯 생성 (14:00 ~ 19:30)
  for (let hour = 14; hour <= 19; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      if (hour === 19 && minute > 30) break; // 19:30까지만
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      timeSlots.push(time);
    }
  }

  // 데이터 조회
  const fetchData = async () => {
    try {
      const [schedulesRes, studentsRes, classesRes, memoRes] = await Promise.all([
        scheduleAPI.getSchedules(),
        studentAPI.getStudents(true), // 활성 학생만
        classAPI.getClasses(),
        memoAPI.getScheduleMemo(),
      ]);
      
      setSchedules(schedulesRes.data.schedules);
      setStudents(studentsRes.data.students);
      setClasses(classesRes.data.classes);
      
      if (memoRes.data.success) {
        setMemo(memoRes.data.memo);
        setMemoLastUpdated(memoRes.data.last_updated);
      }
    } catch (error) {
      console.error('데이터 조회 실패:', error);
      showSnackbar('데이터 조회에 실패했습니다.', 'error');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // 특정 시간대의 스케줄 조회
  const getScheduleForSlot = (dayOfWeek, timeSlot) => {
    return schedules.find(schedule => 
      schedule.day_of_week === dayOfWeek && 
      schedule.start_time === timeSlot
    );
  };

  // 좌석별 스케줄 조회
  const getScheduleForSeat = (dayOfWeek, timeSlot, seatNumber) => {
    // 프론트엔드 요일 인덱스를 백엔드 인덱스로 변환 (화=1, 수=2, 목=3, 금=4, 토=5)
    const backendDayIndex = dayOfWeek + 1;
    
    return schedules.find(schedule => {
      const scheduleStartTime = schedule.start_time;
      const scheduleEndTime = schedule.end_time;
      
      // 시간 슬롯이 스케줄 시간 범위 내에 있는지 확인
      const isInTimeRange = timeSlot >= scheduleStartTime && timeSlot < scheduleEndTime;
      
      return schedule.day_of_week === backendDayIndex && 
             schedule.seat_number === seatNumber &&
             isInTimeRange;
    });
  };

  // 시간대 클릭 핸들러
  const handleTimeSlotClick = (dayOfWeek, timeSlot, seatNumber) => {
    const existingSchedule = getScheduleForSeat(dayOfWeek, timeSlot, seatNumber);
    
    if (existingSchedule) {
      // 이미 배치된 경우 - 수정 다이얼로그 열기
      console.log('기존 스케줄:', existingSchedule);
      console.log('학생 ID:', existingSchedule.student_id);
      console.log('수업 ID:', existingSchedule.class_id);
      
      setSelectedSlot({
        dayOfWeek,
        timeSlot,
        seatNumber,
        start_time: timeSlot,
        scheduleId: existingSchedule.id, // 기존 스케줄 ID 저장
      });
      
      // 기존 학생의 수업 리스트를 먼저 가져온 후 폼 데이터 설정
      const studentClasses = getStudentClasses(existingSchedule.student_id);
      console.log('학생의 수강 수업들:', studentClasses);
      console.log('기존 수업 ID:', existingSchedule.class_id);
      console.log('기존 수업이 학생 수강 목록에 있는가:', studentClasses.find(cls => cls.id === existingSchedule.class_id));
      
      setFormData({
        student_id: existingSchedule.student_id.toString(),
        class_id: existingSchedule.class_id.toString(),
        start_time: existingSchedule.start_time,
      });
      
      setOpenDialog(true);
    } else {
      // 빈 좌석인 경우 - 학생 배치
      setSelectedSlot({ dayOfWeek, timeSlot, seatNumber });
      setFormData({
        student_id: '',
        class_id: '',
        start_time: timeSlot,
      });
      setOpenDialog(true);
    }
  };

  // 학생 배치/수정
  const handleAssignStudent = async () => {
    try {
      // 프론트엔드 요일 인덱스를 백엔드 인덱스로 변환
      const backendDayIndex = selectedSlot.dayOfWeek + 1;
      
      const scheduleData = {
        student_id: parseInt(formData.student_id),
        class_id: parseInt(formData.class_id),
        day_of_week: backendDayIndex,
        seat_number: selectedSlot.seatNumber,
        start_time: formData.start_time,
      };

      if (selectedSlot.scheduleId) {
        // 기존 스케줄 수정
        await scheduleAPI.updateSchedule(selectedSlot.scheduleId, scheduleData);
        showSnackbar('학생 배치가 성공적으로 수정되었습니다.', 'success');
      } else {
        // 새 스케줄 생성
        await scheduleAPI.createSchedule(scheduleData);
        showSnackbar('학생이 성공적으로 배치되었습니다.', 'success');
      }
      
      setOpenDialog(false);
      fetchData();
    } catch (error) {
      console.error('학생 배치 실패:', error);
      showSnackbar('학생 배치에 실패했습니다.', 'error');
    }
  };

  // 스케줄 제거
  const handleRemoveSchedule = async (scheduleId) => {
    try {
      await scheduleAPI.deleteSchedule(scheduleId);
      showSnackbar('학생이 시간표에서 제거되었습니다.', 'success');
      fetchData();
    } catch (error) {
      console.error('스케줄 제거 실패:', error);
      showSnackbar('스케줄 제거에 실패했습니다.', 'error');
    }
  };

  // 학생 정보 조회
  const getStudentInfo = (studentId) => {
    return students.find(student => student.id === studentId);
  };

  // 선택된 학생이 수강 중인 수업 목록 조회
  const getStudentClasses = (studentId) => {
    if (!studentId) return [];
    // studentId를 숫자로 변환하여 일관성 유지
    const numericStudentId = typeof studentId === 'string' ? parseInt(studentId) : studentId;
    const student = getStudentInfo(numericStudentId);
    return student && student.classes ? student.classes : [];
  };

  // 학생 선택 변경 핸들러
  const handleStudentChange = (studentId) => {
    setFormData(prev => ({ 
      ...prev, 
      student_id: studentId,
      class_id: '' // 학생이 변경되면 수업 선택 초기화
    }));
  };

  // 수업 정보 조회
  const getClassInfo = (classId) => {
    return classes.find(cls => cls.id === classId);
  };

  // 스낵바 표시
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // 스낵바 닫기
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // 메모 저장
  const handleSaveMemo = async () => {
    try {
      const response = await memoAPI.saveScheduleMemo(memo);
      if (response.data.success) {
        setMemoLastUpdated(response.data.last_updated);
        showSnackbar('메모가 저장되었습니다.', 'success');
      } else {
        showSnackbar('메모 저장에 실패했습니다.', 'error');
      }
    } catch (error) {
      console.error('메모 저장 실패:', error);
      showSnackbar('메모 저장에 실패했습니다.', 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <ScheduleIcon sx={{ mr: 1 }} />
        <Typography variant="h4" component="h1">
          시간표 관리
        </Typography>
      </Box>

      {/* 시간표 테이블 */}
      <TableContainer component={Paper} sx={{ maxHeight: 500, overflow: 'auto' }}>
        <Table stickyHeader sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '6%', fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #e0e0e0' }}>
                시간
              </TableCell>
              {days.map((day, index) => {
                const headerColors = [
                  '#e8f5e8', // 화요일 - 헤더용 민트
                  '#fff2e8', // 수요일 - 헤더용 오렌지
                  '#f0e8ff', // 목요일 - 헤더용 보라
                  '#e8f8ff', // 금요일 - 헤더용 하늘
                  '#fff8e8'  // 토요일 - 헤더용 노랑
                ];
                return (
                  <TableCell key={index} align="center" sx={{ width: '18.8%', fontWeight: 'bold', backgroundColor: headerColors[index] }}>
                    {day}요일
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {timeSlots.map((timeSlot) => (
              <TableRow key={timeSlot} sx={{ height: 35 }}>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa', height: 35, p: 1, borderRight: '1px solid #e0e0e0' }}>
                  {timeSlot}
                </TableCell>
                {days.map((day, dayIndex) => {
                  const dayColors = [
                    '#f2f8f2', // 화요일 - 더 연한 민트
                    '#fef9f2', // 수요일 - 더 연한 오렌지
                    '#f8f2fe', // 목요일 - 더 연한 보라
                    '#f2f9fe', // 금요일 - 더 연한 하늘
                    '#fefaf2'  // 토요일 - 더 연한 노랑
                  ];
                  return (
                    <TableCell key={`${timeSlot}-${dayIndex}`} sx={{ p: 0, width: '18.8%', height: 35, backgroundColor: dayColors[dayIndex] }}>
                      <Box sx={{ display: 'flex', height: '100%' }}>
                        {[1, 2, 3, 4, 5, 6].map((seatNumber) => {
                          const schedule = getScheduleForSeat(dayIndex, timeSlot, seatNumber);
                          const student = schedule ? getStudentInfo(schedule.student_id) : null;
                          const classInfo = schedule ? getClassInfo(schedule.class_id) : null;
                          
                          return (
                            <Box
                              key={seatNumber}
                              sx={{
                                flex: 1,
                                height: '100%',
                                cursor: 'pointer',
                                backgroundColor: schedule && classInfo ? (classInfo.color || '#90caf9') : 'transparent',
                                borderRight: '1px solid #e0e0e0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&:hover': {
                                  backgroundColor: schedule && classInfo ? (classInfo.color || '#90caf9') : '#e8e8e8',
                                  opacity: 0.8,
                                },
                              }}
                              onClick={() => handleTimeSlotClick(dayIndex, timeSlot, seatNumber)}
                            >
                              {schedule && schedule.start_time === timeSlot && (
                                <Typography variant="caption" align="center" sx={{ fontSize: '0.7rem', lineHeight: 1, m: 0 }}>
                                  <Box sx={{ fontWeight: 'bold', color: '#333' }}>
                                    {student?.name}
                                  </Box>
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>


      {/* 학생 배치 다이얼로그 */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedSlot?.scheduleId ? '시간표 수정' : '학생 배치'} - {days[selectedSlot?.dayOfWeek]}요일 {selectedSlot?.timeSlot} {selectedSlot?.seatNumber}번 좌석
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>학생 선택</InputLabel>
              <Select
                value={formData.student_id}
                onChange={(e) => handleStudentChange(e.target.value)}
                label="학생 선택"
              >
                {students.map((student) => (
                  <MenuItem key={student.id} value={student.id}>
                    {student.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>수업 선택</InputLabel>
              <Select
                value={formData.class_id}
                onChange={(e) => setFormData(prev => ({ ...prev, class_id: e.target.value }))}
                label="수업 선택"
                disabled={!formData.student_id}
              >
                {(() => {
                  const studentClasses = getStudentClasses(formData.student_id);
                  console.log('드롭다운에서 학생 수강 수업들:', studentClasses);
                  console.log('현재 선택된 수업 ID:', formData.class_id);
                  
                  return studentClasses.length > 0 ? (
                    studentClasses.map((cls) => (
                      <MenuItem key={cls.id} value={cls.id}>
                        {cls.name} ({cls.duration}분)
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      {formData.student_id ? '수강 중인 수업이 없습니다' : '학생을 먼저 선택해주세요'}
                    </MenuItem>
                  );
                })()}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="시작 시간"
              type="time"
              value={formData.start_time}
              onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>취소</Button>
          {selectedSlot?.scheduleId && (
            <Button 
              onClick={() => {
                if (window.confirm('정말로 이 학생을 시간표에서 제거하시겠습니까?')) {
                  handleRemoveSchedule(selectedSlot.scheduleId);
                  setOpenDialog(false);
                }
              }}
              color="error"
            >
              삭제
            </Button>
          )}
          <Button 
            onClick={handleAssignStudent} 
            variant="contained"
            disabled={!formData.student_id || !formData.class_id}
          >
            {selectedSlot?.scheduleId ? '수정' : '배치'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 메모 섹션 */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="h2">
              시간표 메모
            </Typography>
            {memoLastUpdated && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                마지막 수정: {new Date(memoLastUpdated).toLocaleString('ko-KR')}
              </Typography>
            )}
          </Box>
          
          <TextField
            fullWidth
            multiline
            rows={4}
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="시간표 관련 메모를 입력하세요..."
            variant="outlined"
            sx={{ mb: 2 }}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSaveMemo}
              disabled={!memo.trim()}
            >
              메모 저장
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ScheduleManagement;
